import { Check } from "lucide-react";
import Mascot from "./Mascot";
import Reveal, { SectionHead } from "./Reveal";

export const CURRENT_PHASE = 2;

const statusOf = (i) => (i + 1 < CURRENT_PHASE ? "completed" : i + 1 === CURRENT_PHASE ? "current" : "upcoming");

const PHASES = [
    {
        tag: "PHASE 1",
        title: "GO BALD 🧑‍🦲",
        items: ["Launch $BALD", "Build the community", "Meme takeover begins", "Meme contests & giveaways"],
        target: "$10K MC",
        env: "border-cyan-400/30 shadow-[0_0_40px_rgba(0,240,255,0.08)]",
        node: "bg-cyan-400 shadow-[0_0_16px_rgba(0,240,255,0.8)]",
        chip: "border-cyan-400/40 text-cyan-300",
        glow: "bg-[radial-gradient(circle_at_50%_0%,rgba(0,240,255,0.10),transparent_70%)]",
    },
    {
        tag: "PHASE 2",
        title: "NO HAIR, NO BRAKES 🔥",
        items: [
            "Grow the holder base",
            "Daily X content",
            "Community raids",
            "Creator collaborations",
            "More community events",
        ],
        target: "$50K MC",
        env: "border-green-400/30 shadow-[0_0_40px_rgba(57,255,20,0.07)]",
        node: "bg-green-400 shadow-[0_0_16px_rgba(57,255,20,0.8)]",
        chip: "border-green-400/40 text-green-300",
        glow: "bg-[radial-gradient(circle_at_50%_0%,rgba(57,255,20,0.09),transparent_70%)]",
    },
    {
        tag: "PHASE 3",
        title: "BALD TAKEOVER 🚀",
        items: [
            "Bigger marketing campaigns",
            "Major meme pushes",
            "Community growth",
            "More collaborations",
        ],
        target: "$100K MC",
        env: "border-purple-400/30 shadow-[0_0_40px_rgba(168,85,247,0.09)]",
        node: "bg-purple-400 shadow-[0_0_16px_rgba(168,85,247,0.8)]",
        chip: "border-purple-400/40 text-purple-300",
        glow: "bg-[radial-gradient(circle_at_50%_0%,rgba(168,85,247,0.11),transparent_70%)]",
    },
    {
        tag: "PHASE 4",
        title: "THE BALD ARMY 👑",
        items: ["Massive X presence", "Bigger creator network", "Community activations", "Global meme campaigns"],
        target: "$250K → $500K MC",
        env: "border-yellow-400/30 shadow-[0_0_40px_rgba(245,197,24,0.08)]",
        node: "bg-yellow-400 shadow-[0_0_16px_rgba(245,197,24,0.8)]",
        chip: "border-yellow-400/40 text-yellow-300",
        glow: "bg-[radial-gradient(circle_at_50%_0%,rgba(245,197,24,0.10),transparent_70%)]",
    },
    {
        tag: "FINAL BOSS",
        title: "$1M MC 💀👑",
        items: [],
        target: "$1M MC",
        env: "border-red-500/40 shadow-[0_0_50px_rgba(255,61,0,0.12)]",
        node: "bg-red-500 shadow-[0_0_16px_rgba(255,61,0,0.8)]",
        chip: "border-red-400/50 text-red-300",
        glow: "bg-[radial-gradient(circle_at_50%_0%,rgba(255,61,0,0.13),transparent_70%)]",
    },
];

function PhaseCard({ p, i }) {
    const final = p.tag === "FINAL BOSS";
    const status = statusOf(i);
    const done = status === "completed";
    const current = status === "current";
    return (
        <div
            data-testid={`roadmap-phase-${i + 1}`}
            data-status={status}
            className={`glass relative flex h-full flex-col gap-4 rounded-2xl border p-6 ${p.env} ${
                current ? "ring-1 ring-green-400/60 animate-pulse-border" : ""
            } ${done ? "border-green-400/30" : ""}`}
        >
            <div className={`pointer-events-none absolute inset-0 rounded-2xl ${p.glow}`} />
            <div className="relative flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest ${p.chip}`}>
                    {p.tag}
                </span>
                {done && (
                    <span data-testid={`roadmap-phase-${i + 1}-status`} className="flex items-center gap-1 rounded-full bg-green-400 px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-black">
                        <Check className="h-3 w-3" /> COMPLETED
                    </span>
                )}
                {current && (
                    <span data-testid={`roadmap-phase-${i + 1}-status`} className="flex items-center gap-1.5 rounded-full border border-green-400/60 bg-green-400/10 px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-widest text-green-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse-dot" /> WE ARE HERE
                    </span>
                )}
            </div>
            <h3 className="relative headline text-xl text-white lg:text-2xl">{p.title}</h3>
            {final ? (
                <p className="relative text-sm font-bold uppercase tracking-widest text-gradient-gold">
                    THE $BALD MILLION CLUB
                </p>
            ) : (
                <ul className="relative space-y-2 text-sm text-slate-300">
                    {p.items.map((it) => (
                        <li key={it} className={`flex gap-2 ${done ? "text-slate-400" : ""}`}>
                            {done ? (
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-400" />
                            ) : (
                                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-60" />
                            )}
                            <span className={done ? "line-through decoration-green-400/50" : ""}>{it}</span>
                        </li>
                    ))}
                </ul>
            )}
            {final && (
                <p className="relative text-sm text-slate-400">No hair. No brakes. Just memes.</p>
            )}
            <div className="relative mt-auto pt-3">
                <span className="text-xs uppercase tracking-widest text-slate-500">
                    {final ? "FINAL MILESTONE" : done ? "MILESTONE REACHED" : "TARGET MILESTONE"}
                </span>
                <div className={`mt-1 inline-block rounded-lg border px-3 py-1.5 font-mono text-sm font-bold ${done ? "border-green-400/50 text-green-300" : p.chip}`}>
                    {done ? "✓ " : ""}{p.target}
                </div>
            </div>
        </div>
    );
}

export default function Roadmap() {
    return (
        <section id="roadmap" data-testid="roadmap-section" className="relative scroll-mt-24 py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
                <SectionHead
                    kicker={`ROADMAP · CURRENTLY IN PHASE ${CURRENT_PHASE}`}
                    title="THE BALD ROADMAP"
                    sub="Phase 1 is done. Market-cap targets are aspirational community milestones — not guarantees."
                />
                <div className="relative mt-16">
                    <div className="absolute left-[10px] top-2 bottom-2 w-px bg-gradient-to-b from-cyan-400/50 via-purple-400/40 to-red-500/50 lg:left-0 lg:right-0 lg:top-[9px] lg:bottom-auto lg:h-px lg:w-full lg:bg-gradient-to-r" aria-hidden="true" />
                    <div className="grid gap-10 lg:grid-cols-5 lg:gap-4">
                        {PHASES.map((p, i) => (
                            <Reveal key={p.tag} delay={i * 0.1} className="relative pl-8 lg:pl-0 lg:pt-10">
                                <span
                                    className={`absolute left-0 top-2 h-[19px] w-[19px] rounded-full border-4 border-[#04060D] lg:left-[calc(100%-9px)] lg:top-0 ${
                                        i === PHASES.length - 1 ? "lg:left-auto lg:right-0" : ""
                                    } ${statusOf(i) === "completed" ? "bg-green-400 shadow-[0_0_16px_rgba(57,255,20,0.8)]" : statusOf(i) === "upcoming" ? "bg-slate-600" : `${p.node} animate-pulse-dot`} ${i < PHASES.length - 1 ? "lg:left-[calc(100%-9px)]" : ""}`}
                                    aria-hidden="true"
                                />
                                <PhaseCard p={p} i={i} />
                            </Reveal>
                        ))}
                    </div>
                    <div className="mt-10 flex justify-center lg:hidden">
                        <Mascot name="gallerySolana" className="hidden" />
                    </div>
                </div>

                <Reveal delay={0.15}>
                    <div
                        data-testid="roadmap-end-station"
                        className="glass relative mt-14 overflow-hidden rounded-3xl border-yellow-400/40 p-8 text-center shadow-[0_0_60px_rgba(245,197,24,0.12)] md:p-12"
                    >
                        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_80%_at_50%_110%,rgba(245,197,24,0.16),transparent)]" />
                        <div className="relative flex flex-col items-center gap-4">
                            <span className="rounded-full border border-yellow-400/50 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-yellow-300">
                                🚉 END STATION
                            </span>
                            <h3 className="headline text-5xl sm:text-6xl lg:text-7xl">
                                <span className="text-gradient-gold">$43M MC</span>
                            </h3>
                            <p className="headline text-xl text-white md:text-3xl">AND EVEN THEN, WE KEEP BUILDING.</p>
                            <p className="max-w-2xl text-sm text-slate-400 md:text-base">
                                $1M is the Million Club. $43M is where the bald pigeon wants to land. Neither is a promise — both are
                                the direction. The train doesn't stop at the end station; it just gets a bigger engine.
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
